import { useCallback, useState } from "react";
import styled from "styled-components";
import { SpotifyIcon } from "@stubhub/broadway-web/icons";
import { Link } from "@viagogo/core/ui";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import { Content, FormatContent } from "src/shared/components/content";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useInView } from "src/shared/hooks/useInView";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { storeAnalyticsEvent } from "src/shared/utils/analyticsevent/analyticsevent-batcher";
import { writeUrl } from "src/shared/utils/url-builder";
import { SpotifyGreenButton } from "src/website-apps/responsive/stubhub-home/components/connect-to-spotify/SpotifyGreenButton";
import { Image } from "src/website-apps/shared/components/image";
import { buildHomeCuratedContentEventBody } from "src/website-apps/shared/utils/homeCuratedContentEvent";
import {
  connectedContainer,
  connectedText,
  helpCenterLink,
  manageButton,
  manageWrapper,
  tooltipContainer,
  tooltipText,
} from "./ConnectToSpotifyBanner.styles";

const PRIMARY_COPY_CONTENT_ID = "cid$#3293268#";
const SECONDARY_COPY_CONTENT_ID = "cid$#3293269#";
const COMPACT_COPY_CONTENT_ID = "cid$#3859597#"; // "Discover events from your favorite Artist"
const BUTTON_CONTENT_ID = "cid$#3293302#";
const CONNECTED_CONTENT_ID = "cid$#4167397#"; // "Spotify Connected"
const CONNECTED_WITH_COUNT_CONTENT_ID = "cid$#4167398#"; // "Spotify Connected · #{0} artists"
const MANAGE_TOOLTIP_CONTENT_ID = "cid$#3304904#";
const MANAGE_TOOLTIP_LINK_CONTENT_ID = "cid$#3304907#";
const SPOTIFY_HELP_ARTICLE_URL = "https://support.spotify.com/us/article/spotify-on-other-apps/";

const Container = styled.div<{ $isCompact?: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  padding: ${({ theme, $isCompact }) => ($isCompact ? theme.spacing(3) : theme.spacing(6))};
  gap: ${({ theme, $isCompact }) => ($isCompact ? theme.spacing(2) : theme.spacing(4.5))};
  background-color: black;
  border-radius: ${({ theme }) => theme.borderRadius.l};
  flex-direction: ${({ $isCompact }) => ($isCompact ? "row" : "column")};
  align-items: center;
  @media only screen and (min-width: ${({ theme }) => theme.media.breakpoints.md}px) {
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing(9)};
  }
`;

const CopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  gap: 3px; // theme's spacing utility operates on multiples of 4px, '3px' > theme.spacing(0.75)
  text-align: center;

  & > p:first-child {
    color: white;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
  }

  & > p:last-child {
    color: #e5e9eb; // not in [colors.ts](https://dev.azure.com/viagogo/Packages/_git/Viagogo.Npm.Core?path=/src/themes/shared/colors.ts)
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
  }

  @media only screen and (min-width: ${({ theme }) => theme.media.breakpoints.md}px) {
    text-align: unset;
    & > p:first-child {
      font-size: 16px;
      font-weight: 700;
      line-height: 24px;
    }

    & > p:last-child {
      font-size: 14px;
      font-weight: 400;
      line-height: 20px;
    }
  }
`;

interface ConnectToSpotifyBannerProps {
  connectUrl: string | null | undefined;
  isConnected: boolean | null;
  importedArtistCount: number | undefined;
  isCompact: boolean;
  /**
   * Experiment variant to stamp on the UAEs. Omit for users who are NOT in the experiment
   * (e.g. non-US, native, Spotify-connected) so they can still be measured but identified as
   * out-of-experiment via a missing `experimentVariant`.
   */
  experimentVariant?: number;
}

export const ConnectToSpotifyBanner = ({
  connectUrl,
  isConnected,
  importedArtistCount,
  isCompact,
  experimentVariant,
}: ConnectToSpotifyBannerProps) => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const { isUserLoggedIn } = appContext;

  const forceAuthOnFavorite = useSimpleFeatureTernary(
    FeatureSetEnum.GlobalForceSignInForFavorites,
    false,
    true,
    () => !isUserLoggedIn,
  );

  const resolvedConnectUrl =
    forceAuthOnFavorite && !isUserLoggedIn
      ? writeUrl("/secure/login", [["ReturnUrl", connectUrl]], appContext)
      : connectUrl;

  const SPOTIFY_LOGO = `${appContext.env["REACT_APP_ASSETS_URL"]}/img/spotify/Spotify_Logo_RGB_Green.png`;
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const toggleTooltip = useCallback(() => {
    setIsTooltipOpen((prev) => !prev);
  }, []);

  const { ref } = useInView({
    triggerOnce: true,
    onChange: (inView) => {
      if (!inView) return;
      storeAnalyticsEvent(
        {
          applicationName: appContext.appName,
          applicationVersion: appContext.appVersion,
          eventName: "HomeCuratedContentImpressions",
          eventBody: buildHomeCuratedContentEventBody(appContext, { entityId: -1, experimentVariant }),
        },
        appContext.env,
      );
    },
  });

  if (isConnected && !connectUrl) {
    return (
      <div className={connectedContainer}>
        <SpotifyIcon size={24} />
        <span className={connectedText}>
          {(importedArtistCount ?? 0) > 0 ? (
            <FormatContent id={CONNECTED_WITH_COUNT_CONTENT_ID} params={[importedArtistCount ?? 0]} />
          ) : (
            <Content id={CONNECTED_CONTENT_ID} />
          )}
        </span>
        <div className={manageWrapper}>
          <button type="button" className={manageButton} onClick={toggleTooltip} aria-label="Manage Spotify connection">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
              <text x="8" y="12" textAnchor="middle" fill="currentColor" fontSize="11" fontWeight="600">
                i
              </text>
            </svg>
          </button>
          {isTooltipOpen && (
            <div className={tooltipContainer}>
              <p className={tooltipText}>
                <Content id={MANAGE_TOOLTIP_CONTENT_ID} />
              </p>
              <Link className={helpCenterLink} url={SPOTIFY_HELP_ARTICLE_URL} newTab>
                <Content id={MANAGE_TOOLTIP_LINK_CONTENT_ID} />
              </Link>
            </div>
          )}
        </div>
      </div>
    );
  }

  return connectUrl ? (
    <Container ref={ref} $isCompact={isCompact}>
      {isCompact ? (
        <SpotifyIcon size={36 as 32} />
      ) : (
        <Image size={{ width: 113, height: 34 }} src={SPOTIFY_LOGO} alt="Spotify Logo" />
      )}
      <CopyContainer>
        {!isCompact && (
          <p>
            <Content id={PRIMARY_COPY_CONTENT_ID} />
          </p>
        )}
        <p>
          <Content id={isCompact ? COMPACT_COPY_CONTENT_ID : SECONDARY_COPY_CONTENT_ID} />
        </p>
      </CopyContainer>
      <SpotifyGreenButton
        onClick={() => {
          storePageEvent(PE_TYPE.CLICK, PE_KEYS.CONNECT_TO_SPOTIFY_LINK, appContext.anonymousId);
          storeAnalyticsEvent(
            {
              applicationName: appContext.appName,
              applicationVersion: appContext.appVersion,
              eventName: "HomeCuratedContentClicks",
              eventBody: buildHomeCuratedContentEventBody(appContext, { entityId: -1, experimentVariant }),
            },
            appContext.env,
          );
        }}
        url={resolvedConnectUrl}
        $isCompact={isCompact}
      >
        <Content id={BUTTON_CONTENT_ID} />
      </SpotifyGreenButton>
    </Container>
  ) : null;
};
